import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const slugify = s => s.toLowerCase()
  .replace(/[àáâãäå]/g,'a').replace(/[èéêë]/g,'e')
  .replace(/[ìíîï]/g,'i').replace(/[òóôõö]/g,'o')
  .replace(/[ùúûü]/g,'u').replace(/[^a-z0-9]+/g,'-')
  .replace(/^-|-$/g,'');

const fmtEur = v => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';
// Usa formatDate da utils per dd/mm/yyyy
const fmtDate = (d) => {
  if (!d) return '—';
  const s = d.slice(0, 10);
  const [y, m, day] = s.split('-');
  return day && m && y ? day + '/' + m + '/' + y : '—';
};

const STATUS_COLORS = {
  planned: '#F0AD4E', in_progress: '#4A7CFF', at_risk: '#F5A623',
  done: '#2EA084', blocked: '#E85D5D',
};
const STATUS_LABEL = {
  planned: 'Pianificata', in_progress: 'In corso', at_risk: 'A rischio',
  done: 'Completata', blocked: 'Bloccata',
};
const TASK_LABEL = {
  todo: 'Da fare', in_progress: 'In corso', done: 'Completata', skipped: 'Saltata',
};

const s = StyleSheet.create({
  page: { paddingTop: 71, paddingBottom: 71, paddingLeft: 40, paddingRight: 40, fontSize: 8, fontFamily: 'Helvetica', color: '#1a1a2e' },
  // Header
  headerBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: '#2EA084' },
  headerWrap: { position: 'absolute', top: 12, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 8, borderBottom: '1pt solid #e9ecef' },
  headerLeft: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#2EA084' },
  headerRight: { fontSize: 8, color: '#868e96', textAlign: 'right' },
  // Footer
  footer: { position: 'absolute', bottom: 24, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#868e96', borderTop: '0.5pt solid #e9ecef', paddingTop: 6 },
  // Sections
  sectionHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 4, paddingBottom: 4, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#0A1628', backgroundColor: '#fff', marginBottom: 6, marginTop: 14 },
  // KPI
  kpiRow: { flexDirection: 'row', marginBottom: 8 },
  kpiBox: { border: '1pt solid #e9ecef', borderRadius: 4, padding: '8 10', flex: 1, margin: '0 4' },
  kpiLabel: { fontSize: 7, color: '#868e96', textTransform: 'uppercase', marginBottom: 2 },
  kpiValue: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  // Table
  tableHead: { flexDirection: 'row', backgroundColor: '#f8f9fa', borderBottom: '1pt solid #e9ecef', padding: '4 6', fontFamily: 'Helvetica-Bold', fontSize: 7 },
  tableRow: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8 },
  tableRowAlt: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8, backgroundColor: '#f8f9fa' },
  tableRowTotal: { flexDirection: 'row', padding: '5 6', fontSize: 8, fontFamily: 'Helvetica-Bold', backgroundColor: '#F5F5F5', color: '#0A1628', borderTop: '1pt solid #2EA084' },
  // Status badge
  badge: { fontSize: 7, padding: '1 4', borderRadius: 2 },
});

const BADGE_STYLES = {
  done:        { backgroundColor: '#E8F5EF', color: '#0F6E56' },
  in_progress: { backgroundColor: '#EEF3FF', color: '#2D4BA0' },
  at_risk:     { backgroundColor: '#FEF6E7', color: '#8B5E00' },
  planned:     { backgroundColor: '#F5F5F5', color: '#555' },
  blocked:     { backgroundColor: '#FDEEEE', color: '#9B1C1C' },
};

export default function PianoLavoriPDF({ piano, cantiere, azienda }) {
  const fasi = piano?.fasi || [];
  const milestone = piano?.milestone || [];
  const oggi = new Date().toLocaleDateString('it-IT');

  const totCostoPrev = fasi.reduce((s, f) => s + Number(f.costo_previsto || 0), 0);
  const totCostoEff = fasi.reduce((s, f) => s + Number(f.costo_effettivo || 0), 0);

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.headerBar} fixed />
        <View style={s.headerWrap} fixed>
          <Text style={s.headerLeft}>Piano Lavori — {cantiere?.titolo || ''}</Text>
          <View style={s.headerRight}>
            <Text>{azienda?.nome || ''}</Text>
            <Text>Generato il {oggi}</Text>
          </View>
        </View>

        {/* SEZIONE 1 — Riepilogo */}
        <Text style={s.sectionHead}>Riepilogo Piano</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Budget totale</Text>
            <Text style={s.kpiValue}>{fmtEur(piano?.budget_totale)}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Budget speso</Text>
            <Text style={[s.kpiValue, { color: '#E85D5D' }]}>{fmtEur(piano?.budget_speso)}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Avanzamento</Text>
            <Text style={[s.kpiValue, { color: '#2EA084' }]}>{piano?.percentuale_avanzamento || 0}%</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Stato</Text>
            <Text style={[s.kpiValue, { color: STATUS_COLORS[piano?.status_semaforo] || '#868e96' }]}>
              {STATUS_LABEL[piano?.status_semaforo] || '—'}
            </Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', gap: 20, marginBottom: 4, fontSize: 8, color: '#868e96' }}>
          <Text>Inizio: {fmtDate(piano?.data_inizio_contrattuale)}</Text>
          <Text>Fine: {fmtDate(piano?.data_fine_contrattuale)}</Text>
          <Text>Durata: {piano?.durata_giorni ? piano.durata_giorni + ' giorni' : '—'}</Text>
        </View>

        {/* SEZIONE 2 — Fasi */}
        <Text style={s.sectionHead}>Fasi ({fasi.length})</Text>
        <View style={s.tableHead}>
          <Text style={{ flex: 3 }}>Fase</Text>
          <Text style={{ flex: 1.5 }}>Stato</Text>
          <Text style={{ flex: 1.5 }}>Inizio</Text>
          <Text style={{ flex: 1.5 }}>Fine</Text>
          <Text style={{ flex: 0.8, textAlign: 'right' }}>%</Text>
          <Text style={{ flex: 1.5, textAlign: 'right' }}>Costo prev.</Text>
          <Text style={{ flex: 1.5, textAlign: 'right' }}>Costo eff.</Text>
        </View>
        {fasi.map((f, i) => (
          <View key={f.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
            <Text style={{ flex: 3 }}>{f.nome}</Text>
            <View style={{ flex: 1.5 }}>
              <Text style={[s.badge, BADGE_STYLES[f.status] || { backgroundColor: '#F5F5F5', color: '#555' }, { alignSelf: 'flex-start' }]}>
                {STATUS_LABEL[f.status] || f.status}
              </Text>
            </View>
            <Text style={{ flex: 1.5 }}>{fmtDate(f.data_inizio_pianificata)}</Text>
            <Text style={{ flex: 1.5 }}>{fmtDate(f.data_fine_pianificata)}</Text>
            <Text style={{ flex: 0.8, textAlign: 'right' }}>{f.percentuale_completamento || 0}%</Text>
            <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(f.costo_previsto)}</Text>
            <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(f.costo_effettivo)}</Text>
          </View>
        ))}
        <View style={s.tableRowTotal}>
          <Text style={{ flex: 3 }}>Totale</Text>
          <Text style={{ flex: 1.5 }} /><Text style={{ flex: 1.5 }} /><Text style={{ flex: 1.5 }} /><Text style={{ flex: 0.8 }} />
          <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(totCostoPrev)}</Text>
          <Text style={{ flex: 1.5, textAlign: 'right' }}>{fmtEur(totCostoEff)}</Text>
        </View>

        {/* SEZIONE 3 — Task per fase */}
        {fasi.filter(f => f.tasks && f.tasks.length > 0).map(f => (
          <View key={'tasks-' + f.id} wrap={false}>
            <Text style={[s.sectionHead, { fontSize: 8 }]}>Task — {f.nome}</Text>
            <View style={s.tableHead}>
              <Text style={{ flex: 3 }}>Task</Text>
              <Text style={{ flex: 1.5 }}>Stato</Text>
              <Text style={{ flex: 1.5 }}>Data prevista</Text>
              <Text style={{ flex: 1.5 }}>Completato il</Text>
            </View>
            {f.tasks.map((t, ti) => {
              const isDone = t.status === 'done';
              const isLate = t.data_prevista && t.completato_il && t.completato_il.slice(0, 10) > t.data_prevista;
              return (
                <View key={t.id} style={ti % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                  <Text style={{ flex: 3, color: isDone ? '#2EA084' : isLate ? '#E85D5D' : '#1a1a2e' }}>{t.nome}</Text>
                  <Text style={{ flex: 1.5 }}>{TASK_LABEL[t.status] || t.status}</Text>
                  <Text style={{ flex: 1.5 }}>{fmtDate(t.data_prevista)}</Text>
                  <Text style={{ flex: 1.5 }}>{t.completato_il ? t.completato_il.slice(0, 10) : '—'}</Text>
                </View>
              );
            })}
          </View>
        ))}

        {/* SEZIONE 4 — Milestone */}
        {milestone.length > 0 && (
          <View>
            <Text style={s.sectionHead}>Milestone ({milestone.length})</Text>
            <View style={s.tableHead}>
              <Text style={{ flex: 3 }}>Nome</Text>
              <Text style={{ flex: 1.5 }}>Data</Text>
              <Text style={{ flex: 1.5, textAlign: 'right' }}>Importo SAL</Text>
              <Text style={{ flex: 1, textAlign: 'center' }}>Raggiunta</Text>
            </View>
            {milestone.map((m, i) => (
              <View key={m.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                <Text style={{ flex: 3 }}>{m.nome}</Text>
                <Text style={{ flex: 1.5 }}>{fmtDate(m.data)}</Text>
                <Text style={{ flex: 1.5, textAlign: 'right' }}>{m.importo_sal ? fmtEur(m.importo_sal) : '—'}</Text>
                <Text style={{ flex: 1, textAlign: 'center', color: m.raggiunta ? '#2EA084' : '#868e96' }}>
                  {m.raggiunta ? 'Si' : 'No'}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Footer */}
        <View style={s.footer} fixed>
          <Text>mAIstro — Piano Lavori</Text>
          <Text>{cantiere?.titolo || ''}</Text>
          <Text render={({ pageNumber, totalPages }) => 'Pagina ' + pageNumber + ' di ' + totalPages} />
        </View>
      </Page>
    </Document>
  );
}

PianoLavoriPDF.slugify = slugify;
export { slugify };
